'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import {
  FormValidationMessages,
  useFormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { z } from 'zod';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../ui/button';
import { FiSend } from 'react-icons/fi';
import { Field } from '@eliadmin/core/src/components/form/floating-label-field/field';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { type ChangeEvent, useRef, useState } from 'react';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { uploadSecretFile } from '@eliadmin/core/src/api/general/general-api';
import { MdOutlineFileUpload } from 'react-icons/md';
import type { TDynamicData } from '../../types/dynamic-data';
import Link from '@eliadmin/core/src/components/link';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

export function Contact({ services }: { services: TDynamicData[] }) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      text: '',
      gdpr: false,
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const MAX_FILE_SIZE = 2 * 1024 * 1024;
  const MAX_FILES = 15;

  const handlePickFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFileError(null);

    if (selectedFiles.length === 0) {
      setFiles([]);
      return;
    }

    if (selectedFiles.length > MAX_FILES) {
      setFileError(`${t('text3457')} ${MAX_FILES}`);
      setFiles([]);
      return;
    }

    const oversizedFiles = selectedFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setFileError(t('text3458'));
      setFiles([]);
      return;
    }

    setFiles(selectedFiles);
  };

  const handleSubmit = form.handleSubmit(async data => {
    try {
      if (files.length > 0) {
        if (files.length > MAX_FILES) {
          setFileError(`${t('text3457')} ${MAX_FILES}`);
          return;
        }

        const attachments: { id: number }[] = [];

        for (const file of files) {
          if (file.size > MAX_FILE_SIZE) {
            setFileError(t('text3458'));
            return;
          }

          const formData = new FormData();
          formData.append('file', file);

          try {
            const res = await uploadSecretFile({
              formData,
              locale,
            });

            if (res.data?.id) {
              attachments.push({ id: res.data.id });
            }
          } catch {
            setFileError(t('text3459'));
            return;
          }
        }

        data.attachments = attachments;
      }

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <section
      id="kontakt"
      className={cn('custom-section', 'pb-6 pt-6 sm:pb-16 sm:pt-8 md:pt-10 lg:pb-24 lg:pt-12')}
    >
      <Form {...form}>
        <form
          onSubmit={handleSubmit}
          className={cn(
            'w-full max-w-[841px]',
            'flex flex-col items-center justify-center gap-4 rounded-[12px] border border-black/10 bg-white p-6 sm:gap-6 sm:p-8 md:p-10 lg:gap-8 lg:p-12',
            'ea-contact__container',
          )}
        >
          <div className="flex w-full flex-col items-center justify-center gap-4">
            <h2 data-ea-visual-tag="h3" className="lg:text[44px] w-full text-center text-[32px] font-bold tracking-tight text-foreground sm:text-4xl">
              {t('text1117')}
            </h2>
            <p className="w-full text-center text-muted-foreground">
              Vyplňte dopytový formulár a získajte cenovú ponuku ešte dnes.
            </p>
          </div>
          <div className="grid w-full grid-cols-12 gap-x-4 gap-y-4 sm:gap-y-6 lg:gap-y-8">
            <Field
              required
              control={form.control}
              name="name"
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6 ' }}
              label="Meno"
            />
            <Field
              required
              control={form.control}
              name="surname"
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6 ' }}
              label="Priezvisko"
            />
            <Field
              required
              control={form.control}
              name="email"
              type="email"
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
              label="E-mail"
            />
            <Field
              control={form.control}
              name="phone"
              className={{ wrapper: 'col-span-12 w-full sm:col-span-6 ' }}
              label={t('text36')}
            />
            <div className="col-span-12 my-4 flex w-full flex-col items-start justify-start gap-2 sm:my-2 lg:my-0">
              <p className="text-sm font-semibold sm:text-base">O akú službu máte záujem? *</p>
              <Field
                control={form.control}
                name="custom.service"
                type="select"
                options={services.map(s => ({ label: s.name, value: s.name }))}
                className={{
                  wrapper: 'col-span-12 w-full',
                  selectTrigger: cn(
                    'border border-border bg-white',
                    'ea-contact-form__service-select',
                  ),
                }}
                label="Vybrať službu"
              />
            </div>
            <Field
              control={form.control}
              name="text"
              className={{ wrapper: 'col-span-12 w-full' }}
              label="Vaša správa/poznámka..."
            />
            <div className="col-span-12 flex w-full flex-col flex-wrap items-start justify-between gap-4 sm:flex-row">
              <p className="text-sm font-semibold sm:text-base">
                Máte projekt alebo nákres strechy?
                <br />
                Nahrajte súbor.
              </p>
              <FormItem
                className={cn(
                  'lex w-fit items-center justify-start gap-4',
                  'ea-contact-form__file-field',
                )}
              >
                <div className="flex w-fit flex-col gap-2">
                  <FormControl>
                    <div className="w-fit">
                      <button
                        type="button"
                        onClick={handlePickFiles}
                        className={cn(
                          'flex min-h-12 w-fit items-center justify-center whitespace-nowrap rounded-md border border-primary bg-background px-4 py-3 text-sm text-primary transition-all hover:bg-primary/5',
                          'ea-contact-form__file-trigger',
                        )}
                      >
                        {files.length > 0 ? (
                          <span>{files.map(file => file.name).join(', ')}</span>
                        ) : (
                          <span className="flex items-center justify-center">
                            <MdOutlineFileUpload size={24} className="mr-2" />
                            {t('text3456')}
                          </span>
                        )}
                      </button>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="hidden"
                      />
                    </div>
                  </FormControl>
                  {fileError && <p className="text-sm text-destructive">{fileError}</p>}
                </div>
              </FormItem>
            </div>
            <FormField
              control={form.control}
              name="gdpr"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'col-span-12 flex items-start',
                    'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                  )}
                >
                  <FormControl>
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                    />
                  </FormControl>
                  <FormLabel className={cn('pt-[3px]', 'ea-contact-form__gdpr-checkbox-label')}>
                    Beriem na vedomie, že moje{' '}
                    <Link
                      target="_blank"
                      href={'/ochrana-osobnych-udajov'}
                      className="font-bold text-primary"
                    >
                      osobné údaje budú spracúvané{' '}
                    </Link>
                    za účelom vybavenia môjho dopytu na základe oprávneného záujmu prevádzkovateľa v
                    súlade s čl. 6 ods. 1 písm. f) Nariadenia GDPR.
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('col-span-12 hidden')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className={cn('w-full sm:w-fit ', 'ea-contact-form__submit')}
            onClick={() => console.log(form.formState.errors)}
            isLoading={form.formState.isSubmitting}
          >
            <FiSend className="mr-2 h-5 w-5" />
            {t('text1118')}
          </Button>
        </form>
      </Form>
    </section>
  );
}
